/**
 * How a questionnaire's structure is read for ordering. Generic so the
 * definition and the builder's draft, which name members differently, share
 * one rule.
 */
export interface TriggerShape<S, I> {
  items(section: S): readonly I[];
  withItems(section: S, items: I[]): S;
  /** The question keys an item answers: its own, or a repeating group's children. */
  answers(item: I): readonly string[];
  /** The question keys a section's or item's conditions test. */
  triggers(node: S | I): readonly string[];
}

/**
 * Every conditional member placed right after what it depends on, so a
 * patient is asked a follow-up straight after the answer that raised it.
 *
 * Nothing crosses a section: a question moves within its own section, and a
 * gated section moves whole, after the section holding its trigger. A question
 * whose trigger sits in an earlier section goes to the top of its own. A
 * trigger in a later section cannot be honoured without splitting sections, so
 * that member stays where it is, as does anything caught in a cycle.
 */
export function orderByTriggers<S, I>(sections: readonly S[], shape: TriggerShape<S, I>): S[] {
  const sectionKeys = (section: S) => shape.items(section).flatMap((item) => shape.answers(item));
  const orderedSections = placeAfterTriggers(sections, sectionKeys, (section) => shape.triggers(section), new Set());
  const earlier = new Set<string>();
  return orderedSections.map((section) => {
    const items = placeAfterTriggers(shape.items(section), shape.answers, shape.triggers, earlier);
    for (const key of sectionKeys(section)) earlier.add(key);
    return shape.withItems(section, items);
  });
}

function placeAfterTriggers<T>(
  nodes: readonly T[],
  keysOf: (node: T) => readonly string[],
  triggersOf: (node: T) => readonly string[],
  earlier: ReadonlySet<string>,
): T[] {
  const owner = new Map<string, T>();
  for (const node of nodes) for (const key of keysOf(node)) owner.set(key, node);
  const local = (node: T) => [
    ...new Set(
      triggersOf(node)
        .map((key) => owner.get(key))
        .filter((trigger): trigger is T => trigger !== undefined && trigger !== node),
    ),
  ];

  const placed = new Set<T>();
  const out: T[] = [];
  const place = (node: T): void => {
    placed.add(node);
    out.push(node);
    for (const candidate of nodes) {
      if (placed.has(candidate)) continue;
      const triggers = local(candidate);
      if (triggers.includes(node) && triggers.every((trigger) => placed.has(trigger))) place(candidate);
    }
  };

  const fromEarlier = (node: T) => local(node).length === 0 && triggersOf(node).some((key) => earlier.has(key));
  for (const node of nodes) if (!placed.has(node) && fromEarlier(node)) place(node);
  for (const node of nodes) if (!placed.has(node) && local(node).length === 0) place(node);
  for (const node of nodes) if (!placed.has(node)) out.push(node);
  return out;
}
